import { Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { clubAdminGuard } from './guards/club-admin.guard';
import { NoAuthGuard } from './guards/no-auth.guard';
import { LandingPartnerComponent } from './pages/landing-partner/landing-partner.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';

export const routes: Routes = [
  {
    // First release is club acquisition — the front door is the for-clubs
    // landing, not the athlete/directory one. The athlete landing is parked at
    // /for-athletes until the directory ships; swap these back to restore it.
    // This is the ONLY eagerly imported page: it is the primary route, so a
    // lazy chunk here would just add a round trip to first paint.
    path: '',
    canActivate: [NoAuthGuard],
    component: LandingPartnerComponent,
  },
  {
    path: 'partners',
    component: LandingPartnerComponent,
  },
  {
    path: 'for-athletes',
    canActivate: [NoAuthGuard],
    loadComponent: () =>
      import('./pages/landing/landing.component').then(
        (m) => m.LandingComponent
      ),
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./pages/contact-page/contact-page.component').then(
        (m) => m.ContactPageComponent
      ),
  },
  {
    // Where athletes land after sign-up while the athlete side is under
    // construction (first release is partner-facing). Public: a just-signed-up
    // athlete may not be email-confirmed / signed in yet.
    path: 'coming-soon',
    loadComponent: () =>
      import('./pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      ),
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./pages/auth/auth.routes').then((m) => m.authRoutes),
  },
  {
    path: 'setup',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/auth/setup/setup.routes').then((m) => m.setupRoutes),
  },
  {
    path: 'settings',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/settings/settings.component').then(
        (m) => m.SettingsComponent
      ),
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/dashboard/dashboard.routes').then(
        (m) => m.dashboardRoutes
      ),
  },
  {
    // Discover — the unified browse + search directory (merges the old Explore +
    // Search pages). /explore and /search redirect here for back-compat.
    // Club directory hidden for the first release — users reach a club only
    // via its own shared /clubs/:id link, not a browsable list. Redirect to
    // home (NoAuthGuard there chains signed-in users on to their dashboard).
    // Restore this loadComponent (and the browse surfaces on the landing +
    // dashboard) to bring the directory back.
    path: 'discover',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'explore',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'favorites',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/favorites/favorites.component').then(
        (m) => m.FavoritesComponent
      ),
  },
  // Profile is hidden for now (does not match the product yet) — redirect any
  // stale link/bookmark to the dashboard. Restore the loadComponent routes when
  // the profile rework ships.
  { path: 'profile', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: 'profile/:userId', redirectTo: 'dashboard' },
  {
    path: 'review/:clubId/:reservationId',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/club-review/club-review-page.component').then(
        (m) => m.ClubReviewPageComponent
      ),
  },
  {
    path: 'find-users',
    loadComponent: () =>
      import('./components/user-search/user-search.component').then(
        (m) => m.UserSearchComponent
      ),
  },
  {
    path: 'clubs/:clubId',
    loadComponent: () =>
      import('./pages/clubs/clubs.component').then((m) => m.ClubsComponent),
  },
  {
    path: 'clubs/:clubId/book',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/booking/booking-page.component').then(
        (m) => m.BookingPageComponent
      ),
  },
  {
    path: 'clubs/:clubId/book/:serviceId',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/booking/booking-page.component').then(
        (m) => m.BookingPageComponent
      ),
  },
  {
    path: 'club/:clubId/settings',
    canActivate: [clubAdminGuard],
    loadComponent: () =>
      import('./pages/club-settings/club-settings.component').then(
        (m) => m.ClubSettingsComponent
      ),
  },
  {
    path: 'club/:clubId/requests',
    canActivate: [clubAdminGuard],
    loadComponent: () =>
      import('./pages/club-requests/club-requests.component').then(
        (m) => m.ClubRequestsComponent
      ),
  },
  {
    path: 'club/:clubId/stats',
    canActivate: [clubAdminGuard],
    loadComponent: () =>
      import('./pages/club-stats/club-stats.component').then(
        (m) => m.ClubStatsComponent
      ),
  },
  {
    // Athlete's own "my requests" view (the return half of the loop).
    path: 'requests',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/my-requests/my-requests.component').then(
        (m) => m.MyRequestsComponent
      ),
  },
  {
    // Venue approval inbox for paid orders (Stripe plan v2 Phase 1b).
    path: 'club/:clubId/orders',
    canActivate: [clubAdminGuard],
    loadComponent: () =>
      import('./pages/club-orders/club-orders.component').then(
        (m) => m.ClubOrdersComponent
      ),
  },
  {
    // Stripe Checkout return leg (must come before the history route).
    path: 'orders/return',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/my-orders/order-return.component').then(
        (m) => m.OrderReturnComponent
      ),
  },
  {
    // Athlete purchase history.
    path: 'orders',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/my-orders/my-orders.component').then(
        (m) => m.MyOrdersComponent
      ),
  },
  {
    path: 'club-selection',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/club-selection/club-selection.component').then(
        (m) => m.ClubSelectionComponent
      ),
  },
  {
    path: 'subscription/success',
    loadComponent: () =>
      import('./pages/subscription/success/success.component').then(
        (m) => m.SubscriptionSuccessComponent
      ),
  },
  {
    path: 'subscription/failure',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/subscription/failure/failure.component').then(
        (m) => m.SubscriptionFailureComponent
      ),
  },
  {
    path: 'search',
    redirectTo: 'discover',
    pathMatch: 'full',
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./pages/terms-of-service/terms-of-service.component').then(
        (m) => m.TermsOfServiceComponent
      ),
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./pages/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
  },
  {
    path: '404',
    component: NotFoundComponent,
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];
